import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ADMIN_CREDENTIALS } from '../config/adminConfig';

// Interface para usuário (Firebase ou Admin)
interface AppUser {
  uid?: string;
  email: string | null;
  displayName?: string | null;
  isAdmin?: boolean;
}

interface FirebaseContextType {
  currentUser: AppUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  isAdmin: boolean;
}

interface FirebaseProviderProps {
  children: ReactNode;
}

const FirebaseContext = createContext<FirebaseContextType | undefined>(undefined);

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};

export const FirebaseProvider: React.FC<FirebaseProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Verificar se há dados do admin no localStorage
    const adminData = localStorage.getItem('adminData');
    if (adminData) {
      const parsedData = JSON.parse(adminData);
      if (parsedData.email === ADMIN_CREDENTIALS.email) {
        setIsAdmin(true);
        setCurrentUser({
          email: ADMIN_CREDENTIALS.email,
          displayName: 'Administrador',
          isAdmin: true
        });
      }
    }

    // Listener do Firebase Auth
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Se não for admin, usar dados do Firebase
        if (!isAdmin) {
          setCurrentUser({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            isAdmin: false
          });
        }
      } else {
        // Se não houver usuário do Firebase e não for admin, limpar estado
        if (!isAdmin) {
          setCurrentUser(null);
        }
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [isAdmin]);

  const signIn = async (email: string, password: string) => {
    try {
      // Verificar se é o admin
      if (email === ADMIN_CREDENTIALS.email && password === ADMIN_CREDENTIALS.password) {
        setIsAdmin(true);
        const adminUser: AppUser = {
          email: ADMIN_CREDENTIALS.email,
          displayName: 'Administrador',
          isAdmin: true
        };
        setCurrentUser(adminUser);
        localStorage.setItem('adminData', JSON.stringify(adminUser));
        return;
      }

      // Se não for admin, tentar login no Firebase
      const result = await signInWithEmailAndPassword(auth, email, password);
      setCurrentUser({
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        isAdmin: false
      });
      setIsAdmin(false);
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      setCurrentUser({
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        isAdmin: false
      });
    } catch (error) {
      console.error('Erro ao criar conta:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Se for admin, limpar dados do localStorage
      if (isAdmin) {
        localStorage.removeItem('adminData');
        setIsAdmin(false);
        setCurrentUser(null);
        return;
      }

      // Se não for admin, fazer logout no Firebase
      await signOut(auth);
      setCurrentUser(null);
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    signIn,
    signUp,
    logout,
    loading,
    isAdmin
  };

  return (
    <FirebaseContext.Provider value={value}>
      {!loading && children}
    </FirebaseContext.Provider>
  );
};